/**
 * Scoped access (guests and agents share one model — see contracts `AccessPolicy`).
 * The server enforces everything; clients use these pure helpers only to hide affordances a member can't use.
 */
import type { Capabilities, Member } from "@bundle/contracts";

export const FULL_CAPABILITIES: Capabilities = {
  dm: "anyone",
  seeDirectory: "all",
  createChannels: true,
  invite: true,
  search: true,
  downloadFiles: true,
  joinHuddles: true,
  mentionBroadcast: true,
  seeEmails: true,
};

/** Defaults for a restricted member whose policy is missing (e.g. role "guest" without `access`). */
export const RESTRICTED_DEFAULTS: Capabilities = {
  dm: "shared_channels",
  seeDirectory: "shared_channels",
  createChannels: false,
  invite: false,
  search: true,
  downloadFiles: true,
  joinHuddles: true,
  mentionBroadcast: false,
  seeEmails: false,
};

export const NO_CAPABILITIES: Capabilities = {
  dm: "none",
  seeDirectory: "shared_channels",
  createChannels: false,
  invite: false,
  search: false,
  downloadFiles: false,
  joinHuddles: false,
  mentionBroadcast: false,
  seeEmails: false,
};

/** Access ended (expiresAt reached) or the member is deactivated. */
export function isExpired(member: Member, now: number): boolean {
  if (member.deactivated) return true;
  const exp = member.access === undefined ? undefined : member.access.expiresAt;
  return exp !== undefined && exp <= now;
}

/** True when the member only sees what was explicitly shared with them. */
export function isRestricted(member: Member): boolean {
  if (member.access !== undefined) return member.access.level === "restricted";
  return member.role === "guest";
}

/** Member from another organization → "External · Org" badge. */
export function isExternal(member: Member): boolean {
  return member.access !== undefined && member.access.organization !== undefined;
}

export function organizationName(member: Member): string | undefined {
  return member.access === undefined || member.access.organization === undefined
    ? undefined
    : member.access.organization.name;
}

export function capabilitiesFor(member: Member, now: number): Capabilities {
  if (isExpired(member, now)) return NO_CAPABILITIES;
  if (member.access !== undefined) return member.access.can;
  return member.role === "guest" ? RESTRICTED_DEFAULTS : FULL_CAPABILITIES;
}

function isSponsorOf(sponsor: Member, member: Member): boolean {
  return member.access !== undefined && member.access.sponsorId === sponsor.userId;
}

/** Whether `from`'s own DM policy allows starting/continuing a DM with `to`. */
function dmAllowedFrom(from: Member, to: Member, sharedChannelIds: string[], now: number): boolean {
  const policy = capabilitiesFor(from, now).dm;
  if (policy === "anyone") return true;
  if (policy === "none") return false;
  if (isSponsorOf(to, from)) return true;
  if (policy === "sponsor_only") return false;
  return sharedChannelIds.length > 0;
}

/**
 * Can `me` DM `other`? Both sides' policies must allow it (a sponsor can always DM their guest/agent unless
 * its policy is "none"). `sharedChannelIds` = channels both are members of.
 */
export function canDm(me: Member, other: Member, sharedChannelIds: string[], now: number): boolean {
  if (me.userId === other.userId) return !isExpired(me, now);
  if (isExpired(me, now) || isExpired(other, now)) return false;
  return dmAllowedFrom(me, other, sharedChannelIds, now) && dmAllowedFrom(other, me, sharedChannelIds, now);
}

/** Can `me` see `other` in the directory / people search? */
export function canSeeMember(me: Member, other: Member, sharedChannelIds: string[], now: number): boolean {
  if (me.userId === other.userId) return true;
  const caps = capabilitiesFor(me, now);
  if (caps.seeDirectory === "all") return true;
  return sharedChannelIds.length > 0 || isSponsorOf(other, me);
}

/** Inviting guests and creating agents: admins/owners, or any full-access member allowed to invite (sponsors). */
export function canSponsor(me: Member, now: number): boolean {
  if (isExpired(me, now) || isRestricted(me) || me.kind !== "human") return false;
  if (me.role === "owner" || me.role === "admin") return true;
  return me.role === "member" && capabilitiesFor(me, now).invite;
}

/** Admins/owners, or the target's sponsor, may change its access. */
export function canManageAccess(me: Member, target: Member, now: number): boolean {
  if (isExpired(me, now) || isRestricted(me)) return false;
  return me.role === "owner" || me.role === "admin" || isSponsorOf(me, target);
}

/**
 * May `me` create top-level pages (Workspace / Private "+", templates, new project boards)? Not restricted members
 * (guests, agents) nor guests with any policy: they only add subpages inside pages shared with them (server rule).
 */
export function canCreateTopLevelPages(me: Member, now: number): boolean {
  return !isExpired(me, now) && !isRestricted(me) && me.role !== "guest";
}

/** Distinct external organization names among `members`, sorted case-insensitively. */
export function externalOrganizations(members: Member[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of members) {
    const org = organizationName(m);
    if (org !== undefined && !seen.has(org)) {
      seen.add(org);
      out.push(org);
    }
  }
  return out.toSorted((a, b) => {
    const x = a.toLowerCase();
    const y = b.toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  });
}
